import React, { useCallback, useState, useRef, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Stack from '@atlaskit/primitives/stack';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Text from '@atlaskit/primitives/text';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
import Heading from '@atlaskit/heading/heading';
import AkImage from '@atlaskit/image/image';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { SpotlightMedia } from '@atlaskit/spotlight/media';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverContent } from '@atlaskit/spotlight/popover-content';

import LoomCrossSellPopupCardImageNewVisual_Light from '../LoomCrossSellPopupCard/images/LoomCrossSellPopupCardImageNewVisual_Light.png';
import LoomCrossSellPopupCardImageNewVisual_Dark from '../LoomCrossSellPopupCard/images/LoomCrossSellPopupCardImageNewVisual_Dark.png';

const i18n = defineMessages({
	titleForPageHeader: {
		id: 'loom-utils.loom-spotlight-wrapper.title-for-page-header',
		defaultMessage: 'Share your work with video',
		description: 'Title for the Loom spotlight for the page header touch point',
	},
	descriptionForPageHeader: {
		id: 'loom-utils.loom-spotlight-wrapper.description-for-page-header',
		defaultMessage:
			'Save time and engage your team with a video walkthrough, demo, update, and more.',
		description: 'Description for the Loom spotlight for the page header touch point',
	},
	titleForInlineComment: {
		id: 'loom-utils.loom-spotlight-wrapper.title-for-inline-comment',
		defaultMessage: 'Add more context with video',
		description: 'Title for the Loom spotlight for the inline comment touch point',
	},
	descriptionForInlineComment: {
		id: 'loom-utils.loom-spotlight-wrapper.description-for-inline-comment',
		defaultMessage: 'Give your team more context so they can quickly understand and respond.',
		description: 'Description for the Loom spotlight for the inline comment touch point',
	},
	primaryAction: {
		id: 'loom-utils.loom-spotlight-wrapper.primary-action',
		defaultMessage: 'Try now',
		description: 'Primary action button text for the Loom auto-provisioning spotlight',
	},
});

const TOGGLE_DELAY_TIME_MS = 400;

const spotlightContentContainerStyles = xcss({
	position: 'fixed',
});

interface LoomSpotlightWrapperProps {
	children: React.ReactElement;
	touchpoint: 'page-header' | 'inline-comment';
	onSpotlightPrimaryActionClick?: () => void;
	onSpotlightClose?: () => void;
}

export const LoomSpotlightWrapper = ({
	children,
	touchpoint,
	onSpotlightPrimaryActionClick,
	onSpotlightClose,
}: LoomSpotlightWrapperProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { colorMode } = useThemeObserver();
	const isDarkMode = colorMode === 'dark';
	const intl = useIntl();

	// Spotlight state management for Loom auto-provisioning spotlight
	const [isOpen, setIsOpen] = useState(false);
	const closePopupTimerRef = useRef<NodeJS.Timeout | null>(null);
	const openPopupTimerRef = useRef<NodeJS.Timeout | null>(null);
	const triggerElementRef = useRef<HTMLElement | null>(null);

	const clearClosePopupTimer = useCallback(() => {
		if (closePopupTimerRef.current) {
			clearTimeout(closePopupTimerRef.current);
			closePopupTimerRef.current = null;
		}
	}, []);

	const clearOpenPopupTimer = useCallback(() => {
		if (openPopupTimerRef.current) {
			clearTimeout(openPopupTimerRef.current);
			openPopupTimerRef.current = null;
		}
	}, []);

	const safeClosePopup = useCallback(() => {
		setIsOpen(false);
	}, []);

	const startClosePopupTimer = useCallback(() => {
		clearOpenPopupTimer();
		if (!closePopupTimerRef.current) {
			closePopupTimerRef.current = setTimeout(() => {
				safeClosePopup();
				closePopupTimerRef.current = null;
			}, TOGGLE_DELAY_TIME_MS);
		}
	}, [clearOpenPopupTimer, safeClosePopup]);

	const startOpenPopupTimer = useCallback(() => {
		clearClosePopupTimer();
		if (!openPopupTimerRef.current) {
			openPopupTimerRef.current = setTimeout(() => {
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						name: 'loomAutoProvisionedSpotlight',
						attributes: {
							touchpoint,
						},
					},
				}).fire();

				setIsOpen(true);
				openPopupTimerRef.current = null;
			}, TOGGLE_DELAY_TIME_MS);
		}
	}, [clearClosePopupTimer, createAnalyticsEvent, touchpoint]);

	const handleTriggerMouseEnter = useCallback(() => {
		startOpenPopupTimer();
	}, [startOpenPopupTimer]);

	const handleCardMouseEnter = useCallback(() => {
		clearClosePopupTimer();
	}, [clearClosePopupTimer]);

	const handleMouseLeave = useCallback(() => {
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		if (document.activeElement !== triggerElementRef.current) {
			startClosePopupTimer();
		}
	}, [startClosePopupTimer]);

	const handleDismiss = useCallback(() => {
		onSpotlightClose?.();
		setIsOpen(false);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'loomAutoProvisionedSpotlightDismiss',
				source: 'loomAutoProvisionedSpotlight',
				attributes: {
					touchpoint,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, onSpotlightClose, touchpoint]);

	const getTitle = useCallback(() => {
		return touchpoint === 'page-header'
			? intl.formatMessage(i18n.titleForPageHeader)
			: intl.formatMessage(i18n.titleForInlineComment);
	}, [touchpoint, intl]);

	const getDescription = useCallback(() => {
		return touchpoint === 'page-header'
			? intl.formatMessage(i18n.descriptionForPageHeader)
			: intl.formatMessage(i18n.descriptionForInlineComment);
	}, [touchpoint, intl]);

	const targetWithHoverHandlers = React.cloneElement(children, {
		onMouseEnter: handleTriggerMouseEnter,
		onMouseLeave: handleMouseLeave,
	});

	useEffect(() => {
		// clean up timers when the component unmounts
		return () => {
			clearClosePopupTimer();
			clearOpenPopupTimer();
		};
	}, [clearClosePopupTimer, clearOpenPopupTimer]);

	const handleSpotlightPrimaryActionClick = useCallback(() => {
		onSpotlightPrimaryActionClick?.();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'loomAutoProvisionedSpotlightTryNow',
				source: 'loomAutoProvisionedSpotlight',
				attributes: {
					touchpoint,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, onSpotlightPrimaryActionClick, touchpoint]);

	return (
		<PopoverProvider>
			<PopoverTarget>{targetWithHoverHandlers}</PopoverTarget>

			<Box
				ref={triggerElementRef}
				testId="loom-button-spotlight-content-container"
				onMouseEnter={handleCardMouseEnter}
				onMouseLeave={handleMouseLeave}
				xcss={spotlightContentContainerStyles}
			>
				<PopoverContent
					isVisible={isOpen}
					placement="left-start"
					dismiss={handleDismiss}
					testId="loom-button-spotlight-content"
					strategy="absolute"
					shouldDismissOnClickOutside={false}
				>
					<SpotlightCard testId="loom-button-spotlight-content-card">
						<SpotlightHeader>
							<Heading as="span" size="xsmall">
								{getTitle()}
							</Heading>
							<SpotlightControls>
								<SpotlightDismissControl testId="loom-button-spotlight-dismiss-button" />
							</SpotlightControls>
						</SpotlightHeader>
						<SpotlightMedia>
							<AkImage
								alt=""
								src={
									isDarkMode
										? LoomCrossSellPopupCardImageNewVisual_Dark
										: LoomCrossSellPopupCardImageNewVisual_Light
								}
							/>
						</SpotlightMedia>
						<SpotlightBody>
							<Stack space="space.100">
								<Text as="p">{getDescription()}</Text>
							</Stack>
						</SpotlightBody>
						<SpotlightFooter>
							<SpotlightActions>
								{onSpotlightPrimaryActionClick && (
									<SpotlightPrimaryAction onClick={handleSpotlightPrimaryActionClick}>
										{intl.formatMessage(i18n.primaryAction)}
									</SpotlightPrimaryAction>
								)}
							</SpotlightActions>
						</SpotlightFooter>
					</SpotlightCard>
				</PopoverContent>
			</Box>
		</PopoverProvider>
	);
};
